/**
 * InheritedRelationshipsBanner
 *
 * Shown on a successor item's detail page after a lineage action transferred
 * relationships to it. Reads from LineageRelationshipResolution records whose
 * metadata contains this item as `targetItemId`.
 *
 * Per canon 07B §5 — Successor Seeding. Informational only; does not gate
 * any actions. The operator may review and dissolve any inherited relationship
 * they consider inappropriate via the normal relationship management UI.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightLeft,
  ChevronDown,
  ChevronUp,
  X,
  GitBranch,
  ExternalLink,
  Info,
} from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import type { LineageRelationshipResolution } from '../../../types/prioritization/itemLineage';

// ============================================================================
// Types
// ============================================================================

interface TransferRecord {
  resolution: LineageRelationshipResolution;
  typeLabel: string;
  sourceItemId: string;
  relationshipId: string;
  source: 'group' | 'edge';
  reasonCode: string;
}

// ============================================================================
// Helpers
// ============================================================================

function parseResolutionNote(note: string | undefined): Record<string, string> | null {
  if (!note) return null;
  try {
    return JSON.parse(note) as Record<string, string>;
  } catch {
    return null;
  }
}

function getTypeLabel(reasonCode: string, source: 'group' | 'edge'): string {
  if (reasonCode.includes('CLUSTER')) return 'Cluster';
  if (reasonCode.includes('BLOC')) return 'Package';
  if (reasonCode.includes('COLLECTION')) return 'Collection';
  if (reasonCode.includes('DEPENDENCY')) return 'Dependency';
  if (reasonCode.includes('PREREQUISITE')) return 'Prerequisite';
  if (reasonCode.includes('AGGREGATION')) return 'Aggregation';
  if (reasonCode.includes('BREAKDOWN')) return 'Breakdown';
  if (reasonCode.includes('ELABORATION')) return 'Elaboration';
  return source === 'group' ? 'Group relationship' : 'Edge relationship';
}

// ============================================================================
// Component
// ============================================================================

interface InheritedRelationshipsBannerProps {
  itemId: string;
}

export function InheritedRelationshipsBanner({ itemId }: InheritedRelationshipsBannerProps) {
  const resolutions = useItemRelationshipsStore((s) => s.lineageRelationshipResolutions);
  const getItemById = useItemsStore((s) => s.getItemById);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Find all transfer resolution records where this item is the target
  const transfers = useMemo((): TransferRecord[] => {
    return resolutions
      .filter((r) => r.resolutionAction === 'transfer')
      .map((r) => {
        const meta = parseResolutionNote(r.resolutionNote);
        if (!meta || meta.targetItemId !== itemId) return null;
        return {
          resolution: r,
          typeLabel: getTypeLabel(meta.reasonCode ?? '', r.relationshipSource),
          sourceItemId: meta.sourceItemId ?? '',
          relationshipId: r.relationshipId,
          source: r.relationshipSource,
          reasonCode: meta.reasonCode ?? '',
        } satisfies TransferRecord;
      })
      .filter((t): t is TransferRecord => t !== null);
  }, [resolutions, itemId]);

  if (transfers.length === 0 || dismissed) return null;

  return (
    <div className="border border-[var(--slate)] bg-[var(--slate-dim)] overflow-hidden">
      {/* Header row */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--slate-dim)]/50 transition-colors"
      >
        <ArrowRightLeft className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[var(--text-sec)]">
              Inherited relationships
            </span>
            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]">
              {transfers.length}
            </span>
          </div>
          <p className="text-xs text-[var(--text-sec)] mt-0.5">
            {transfers.length} relationship{transfers.length !== 1 ? 's were' : ' was'} transferred to this item from a lineage action.
            Review and dissolve any that shouldn't apply.
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-[var(--text-ter)]" />
          ) : (
            <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
            className="p-1 rounded hover:bg-[var(--slate-dim)] text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-[var(--slate)] divide-y divide-purple-100">
          {transfers.map((t) => {
            const sourceItem = getItemById(t.sourceItemId);
            return (
              <div key={t.resolution.id} className="px-4 py-3 flex items-start gap-3">
                <Info className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide">
                      {t.typeLabel}
                    </span>
                    <span className="text-xs font-mono text-[var(--text-sec)]">{t.reasonCode}</span>
                  </div>
                  {sourceItem ? (
                    <p className="text-xs text-[var(--text-sec)]">
                      Transferred from{' '}
                      <Link
                        to={`/prioritization/items/${t.sourceItemId}`}
                        className="font-medium hover:underline"
                      >
                        {sourceItem.title}
                      </Link>
                    </p>
                  ) : (
                    <p className="text-xs text-[var(--text-sec)]">Source item no longer accessible</p>
                  )}
                  <p className="text-xs text-[var(--text-sec)]">
                    Resolved {new Date(t.resolution.resolvedAt).toLocaleDateString()}
                  </p>
                </div>
                <Link
                  to={`/prioritization/items/${itemId}?tab=relationships`}
                  className="p-1 text-[var(--text-sec)] hover:text-[var(--text-pri)] flex-shrink-0 transition-colors"
                  title="View relationships"
                >
                  <GitBranch className="h-3.5 w-3.5" />
                </Link>
                {t.sourceItemId && (
                  <Link
                    to={`/prioritization/items/${t.sourceItemId}`}
                    className="p-1 text-[var(--text-sec)] hover:text-[var(--text-pri)] flex-shrink-0 transition-colors"
                    title="View source item"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            );
          })}

          <div className="px-4 py-2.5 bg-[var(--slate-dim)]/50">
            <p className="text-xs text-[var(--text-sec)]">
              To remove an inherited relationship, open the relevant relationship type from the sidebar
              (e.g. Dependencies, Aggregations) or review edges on the{' '}
              <Link to={`/prioritization/items/${itemId}?tab=relationships`} className="underline hover:text-[var(--text-ter)]">
                Relationships
              </Link>{' '}
              tab.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
